import { useEffect, useMemo } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, DatePicker, Alert, Button, message,
} from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { changedFields } from '../../lib/changedFields';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useAuthStore } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { LocationPicker } from '../../components/LocationPicker';
import { STATE_OPTIONS } from '../../constants/indianStates';
import { mobileRules, pincodeRules } from '../../lib/validators';
import { PhoneInput } from '../../components/PhoneInput';
import { territoryOption } from '../../lib/territoryLabel';

interface Props {
  open: boolean;
  onClose: () => void;
  record?: any;
  /** Called with the created/updated farmer after a successful save (e.g. to link it to a visit). */
  onSaved?: (farmer: any) => void;
  /** How a NEW farmer came in — SALES when a rep registers one from My Field Day (default APP). */
  defaultRegisteredVia?: string;
}

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिंदी' },
  { value: 'mr', label: 'मराठी' },
  { value: 'bn', label: 'বাংলা' },
  { value: 'or', label: 'ଓଡ଼ିଆ' },
];

const CROP_OPTIONS = [
  'Paddy', 'Wheat', 'Maize', 'Cotton', 'Sugarcane', 'Soybean', 'Groundnut',
  'Pulses', 'Mustard', 'Banana', 'Mango', 'Tomato', 'Onion', 'Potato', 'Chilli',
  'Turmeric', 'Tea', 'Coffee',
];

export function FarmerFormModal({ open, onClose, record, onSaved, defaultRegisteredVia = 'APP' }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const isEdit = !!record;
  const role = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);

  // Owner-assignment behaves by the registrant's role:
  //   SP / RETAILER → auto (self / retailer's SP)   — no picker
  //   SALES_HEAD    → pick a SP from their own team
  //   ADMIN / SUPER → pick a Sales Head, then a SP under that head
  const isAutoOwner = role === 'SALES_PERSON' || role === 'RETAILER';
  const isSH = role === 'SALES_HEAD';
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const needsPicker = isSH || isAdmin;

  const { data: salesUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: open && needsPicker,
  });
  // Territories the actor may register a farmer in (full-access roles get all); limited to
  // district/beat. SP/SH must place the farmer inside their assigned area.
  const { data: myTerritories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: open,
  });
  const isField = role === 'SALES_PERSON' || role === 'SALES_HEAD';
  const allSps: any[] = salesUsers?.data ?? [];
  const spsUnderMe = useMemo(() => allSps.filter((u) => String(u.reportsTo) === String(myId)), [allSps, myId]);

  // The owner picker (SH/Admin) is driven by the selected territory — only SPs who work it.
  const territoryId = Form.useWatch('territoryId', form);
  const assignedSpId = Form.useWatch('assignedSalesPersonId', form);
  const { data: spForTerr, isFetching: spLoading } = useQuery({
    queryKey: ['sps-for-territory', territoryId],
    queryFn: () => request<{ salesPersons: Array<{ id: string; fullName: string; territory?: string }>; defaultOwnerId: string | null }>({ url: '/territory-assignments/sales-persons', params: { territoryId } }),
    enabled: open && needsPicker && !!territoryId,
  });

  // Territory dropdown: district/beat only; keep the record's current territory visible on edit.
  let territoryOptions = (myTerritories ?? [])
    .filter((t: any) => t.level === 'district' || t.level === 'beat')
    .map((t: any) => ({ value: String(t.id), ...territoryOption(t) }));
  if (record?.territoryId && !territoryOptions.some((o: any) => o.value === String(record.territoryId))) {
    const cur = (myTerritories ?? []).find((t: any) => String(t.id) === String(record.territoryId));
    if (cur) territoryOptions = [...territoryOptions, { value: String(cur.id), ...territoryOption(cur) }];
  }

  // Group the territory options by STATE (from the real hierarchy, so beats group under their state
  // too — not by code prefix) so the dropdown reads state-by-state instead of a flat, interleaved list.
  const { data: terrTree } = useQuery({
    queryKey: ['territories-tree-picker'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
    enabled: open,
  });
  const stateOfTerr = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (node: any, stateName: string | null) => {
      const s = node.level === 'state' ? node.name : stateName;
      if (s) map.set(String(node.id), s);
      (node.children ?? []).forEach((cn: any) => walk(cn, s));
    };
    (terrTree ?? []).forEach((n: any) => walk(n, null));
    return map;
  }, [terrTree]);
  const groupedTerritoryOptions = (() => {
    const byState = new Map<string, any[]>();
    for (const opt of territoryOptions) {
      const st = stateOfTerr.get(String(opt.value)) ?? 'Other';
      if (!byState.has(st)) byState.set(st, []);
      byState.get(st)!.push(opt);
    }
    return [...byState.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([state, opts]) => ({ label: state, options: opts.slice().sort((x, y) => String(x.label).localeCompare(String(y.label))) }));
  })();

  // SP options: those working the territory (with territory label); else fallback — SH → own
  // team, Admin → all SPs.
  const scopedSps = territoryId && spForTerr?.salesPersons?.length ? spForTerr.salesPersons : null;
  let spOptions = scopedSps
    ? scopedSps.map((s) => ({ value: String(s.id), label: s.territory ? `${s.fullName} — ${s.territory}` : s.fullName }))
    : (isSH ? spsUnderMe : allSps).map((u: any) => ({ value: String(u.id), label: u.fullName }));
  if (assignedSpId && !spOptions.some((o: any) => o.value === String(assignedSpId))) {
    const found = allSps.find((u: any) => String(u.id) === String(assignedSpId));
    if (found) spOptions = [...spOptions, { value: String(found.id), label: found.fullName }];
  }

  // Seed from `record` via initialValues + a per-target key (below), NOT setFieldsValue in an open-effect:
  // the ui Form seeds its store from initialValues on mount and the Radix Modal remounts the form each
  // open, so the effect path raced the remount (blank edit form) and let a prior farmer's values leak in.
  const initialValues = useMemo(
    () =>
      record
        ? {
            ...record,
            assignedSalesPersonId: record.assignedSalesPersonId ? String(record.assignedSalesPersonId) : undefined,
            territoryId: record.territoryId ? String(record.territoryId) : undefined,
            dob: record.dob ? dayjs(record.dob) : null,
            crops: record.crops ?? [],
            // Older farmers only ever had the single column; show that document rather than an
            // empty field that reads as "no KYC on file".
            kycDocUrls: record.kycDocUrls?.length
              ? record.kycDocUrls
              : (record.kycDocUrl ? [record.kycDocUrl] : []),
            landAcres: record.landAcres ? Number(record.landAcres) : undefined,
          }
        : { languagePref: 'hi', registeredVia: defaultRegisteredVia },
    [record, defaultRegisteredVia],
  );
  const formKey = record?.id ? `edit-${record.id}` : 'create';

  // Pre-select the territory's primary SP for the owner picker (when empty).
  useEffect(() => {
    if (!open || !needsPicker || !territoryId) return;
    const def = spForTerr?.defaultOwnerId;
    if (def && !form.getFieldValue('assignedSalesPersonId')) form.setFieldValue('assignedSalesPersonId', String(def));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsPicker, territoryId, spForTerr?.defaultOwnerId]);

  // The form's values → the request body. An edit shapes its starting values the same way, so the
  // two compare like for like (a date picker value vs the stored date string, 2.5 vs "2.50").
  const toPayload = (vals: any) => ({
    ...vals,
    dob: vals.dob ? vals.dob.format('YYYY-MM-DD') : null,
    landAcres: vals.landAcres !== undefined && vals.landAcres !== null ? String(Number(vals.landAcres)) : undefined,
  });

  const save = useConfirmedMutation({ title: "Save this farmer?", okText: "Yes, save" }, {
    mutationFn: (payload: any) => (isEdit
      ? request({ url: `/farmers/${record.id}`, method: 'PATCH', data: payload })
      : request({ url: '/farmers', method: 'POST', data: payload })),
    onSuccess: (created: any) => {
      message.success(isEdit ? 'Farmer updated' : 'Farmer registered');
      qc.invalidateQueries({ queryKey: ['farmers'] });
      onSaved?.(created);
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit · ${record?.fullName}` : 'Register farmer'}
      width={820}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Register'}</Button>
      </>}
    >
      <Form key={formKey} form={form} initialValues={initialValues} layout="vertical" onFinish={(v) => {
        if (!isEdit) { save.mutate(toPayload(v)); return; }
        // An edit sends only what changed. It used to PATCH the whole record back — including
        // "Registered via", which the server owns — so every save was refused, e.g. an imported
        // farmer's territory could not be changed ("registeredVia must be one of …").
        const patch = changedFields(toPayload(initialValues), toPayload(v), Object.keys(v).filter((k) => k !== 'registeredVia'));
        if (!Object.keys(patch).length) { message.info('Nothing has changed.'); return; }
        save.mutate(patch);
      }}>
        {/* Tailwind 12-col grid: fields stack on narrow screens, so the phone pickers (flag + number)
            always get at least half-width and never truncate. Vertical rhythm from Form.Item margins. */}
        <div className="grid grid-cols-12 items-start gap-x-4">
          <div className="col-span-12">
            <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
              <Input placeholder="Ramesh Patil" />
            </Form.Item>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Form.Item name="mobile" label="Mobile (WhatsApp)" rules={mobileRules()}
              tooltip="The single number for this farmer — used for WhatsApp OTP login and all WhatsApp contact.">
              <PhoneInput placeholder="Phone number" />
            </Form.Item>
          </div>

          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="languagePref" label="Language" rules={[{ required: true }]}>
              <Select options={LANGS} />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="gender" label="Gender">
              <Select
                allowClear
                options={['Male', 'Female', 'Other'].map((g) => ({ value: g, label: g }))}
              />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="dob" label="DOB">
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            {/* Set once, when the farmer is created — the server never lets an edit change it. */}
            <Form.Item name="registeredVia" label="Registered via"
              extra={isEdit ? 'Set when the farmer was registered.' : undefined}>
              <Select
                disabled={isEdit}
                options={['APP', 'RETAILER', 'SALES', 'EVENT', ...(isEdit ? ['IMPORT'] : [])].map((v) => ({ value: v, label: v }))}
              />
            </Form.Item>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <Form.Item name="territoryId" label="Territory"
              rules={isField ? [{ required: true, message: 'Select a territory within your area' }] : []}
              extra={isField ? 'Districts & beats in your area.' : 'Optional — the district/beat this farmer belongs to.'}>
              <Select
                allowClear={!isField}
                showSearch
                optionFilterProp="label"
                placeholder="Select territory"
                options={groupedTerritoryOptions}
                onChange={() => { if (needsPicker) form.setFieldValue('assignedSalesPersonId', undefined); }}
              />
            </Form.Item>
          </div>
          {needsPicker && (
            <div className="col-span-12 sm:col-span-6">
              <Form.Item name="assignedSalesPersonId" label={isSH ? 'Assign to Sales Person (optional)' : 'Assign to Sales Person'}
                rules={isSH ? [] : [{ required: true, message: 'Select a sales person' }]}
                extra={isSH
                  ? 'Leave blank to keep it with the beat’s owner — yourself when you own the beat.'
                  : (territoryId
                    ? (scopedSps ? 'Sales Persons who work the selected territory.' : (!spLoading ? 'No Sales Person works this territory yet — showing all.' : undefined))
                    : 'Pick a territory first to see who works it.')}>
                <Select showSearch optionFilterProp="label" allowClear={isSH}
                  placeholder={territoryId ? 'Select sales person' : 'Pick a territory first'}
                  notFoundContent={territoryId && spLoading ? 'Loading…' : (isSH ? 'No sales persons in your team' : 'None found')}
                  options={spOptions} />
              </Form.Item>
            </div>
          )}
          {isAutoOwner && (
            <div className="col-span-12">
              <Alert type="info" className="mb-2"
                title={role === 'SALES_PERSON'
                  ? 'This farmer will be assigned to you (your customer).'
                  : "This farmer will be assigned to your retailer's sales person."} />
            </div>
          )}

          <div className="col-span-12">
            <LocationPicker fields={{ address: 'address', city: 'city', pincode: 'pincode', state: 'stateCode' }} />
          </div>
          <div className="col-span-12">
            <Form.Item name="address" label="Address">
              <Input.TextArea rows={2} />
            </Form.Item>
          </div>

          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="city" label="City / Town">
              <Input />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="stateCode" label="State">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={STATE_OPTIONS}
              />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="pincode" label="Pincode" rules={pincodeRules(false)}>
              <Input maxLength={6} />
            </Form.Item>
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Form.Item name="landAcres" label="Land (acres)">
              <InputNumber className="w-full" min={0} step={0.1} />
            </Form.Item>
          </div>

          {/* Coordinates come from the map picker above (hidden → no grid cell). */}
          <Form.Item name="latitude" hidden><Input /></Form.Item>
          <Form.Item name="longitude" hidden><Input /></Form.Item>
          <div className="col-span-12">
            <Form.Item name="crops" label="Crops">
              <Select
                mode="tags"
                placeholder="Select or type"
                options={CROP_OPTIONS.map((c) => ({ value: c, label: c }))}
              />
            </Form.Item>
          </div>

          <div className="col-span-12 mb-2 mt-1 flex items-center gap-2">
            <span className="type-overline text-subtle">Documents</span>
            <span className="h-px flex-1 bg-line" />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Form.Item name="photoUrl" label="Farmer photo"
              extra="Used as the avatar across the app + for OTP-recovery identity verification.">
              <FileUploadField category="farmer-photo" buttonLabel="Upload photo" />
            </Form.Item>
          </div>
          <div className="col-span-12 sm:col-span-6">
            {/*
              * KYC takes SEVERAL files — an Aadhaar has a front and a back, and uploading the second
              * used to silently replace the first. It also used to render `preview={false}`, so the
              * one document on file showed as a URL string and nothing else: no way to check you had
              * attached the right scan without opening a new tab.
              */}
            <Form.Item name="kycDocUrls" label="KYC documents (Aadhaar / Voter ID)"
              extra="Front and back both belong here. Stored privately — only shown to admins on the farmer's detail page.">
              <MultiFileUploadField category="attachment" max={6} buttonLabel="Upload KYC" />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
}
